import {
    DataTypes,
    DimensionAggregationStrategies,
} from 'business/definitions';
import type { IndicatorQueryT } from 'modules/decision/types/indicators';

import { useSentenceContext } from '../components/Contexts/SentenceContext/SentenceContext';
import { queryToSentence } from '../components/Sentence/helpers';
import { useTriggerQuery } from '../components/Sentence/helpers/queries';
import { SentenceActionType } from '../components/Sentence/hooks/reducer';
import { createEmptyFilterTree, mapTree } from '../helpers/filterTree';

/** Fill the sentence with the indicator's query and run it. */
export const useApplyIndicator = () => {
    const { dispatchSentence } = useSentenceContext();
    const triggerQuery = useTriggerQuery();

    return (indicator: IndicatorQueryT): SentenceQueryT => {
        // A chart payload may carry no tree flag: derive it from the type, with the same
        // default strategy as when the user picks a tree dimension by hand
        const dimensions = indicator.dimensions.map(
            ({ scale, aggregationStrategy, ...rest }) => {
                const isTree = rest.type === DataTypes.tree;
                const strategy =
                    aggregationStrategy ||
                    (isTree ? DimensionAggregationStrategies.flat : null);
                return {
                    ...rest,
                    isTree,
                    ...(scale ? { scale } : {}),
                    ...(strategy ? { aggregationStrategy: strategy } : {}),
                };
            },
        );
        const filters = indicator.filterTree
            ? mapTree(indicator.filterTree, ({ valueScale, ...rest }) => ({
                  ...rest,
                  ...(valueScale ? { valueScale } : {}),
              }))
            : createEmptyFilterTree<ExploreFilterT>();
        const sentenceQuery = queryToSentence({
            metrics: indicator.metrics,
            dimensions,
            filters,
        });
        dispatchSentence({
            type: SentenceActionType.setSentence,
            query: sentenceQuery,
        });
        triggerQuery(sentenceQuery);
        return sentenceQuery;
    };
};
